/*========================================================================
 * $Id$
 *
 * Copyright © Mindbreeze Software GmbH, Linz, Austria, 2005-2024
 *
 * Der Nutzer des Computerprogramms anerkennt, dass der oben stehende
 * Copyright-Vermerk im Sinn des Welturheberrechtsabkommens an der vom
 * Urheber festgelegten Stelle in der Funktion des Computerprogramms
 * angebracht bleibt, um den Vorbehalt des Urheberrechtes genuegend zum
 * Ausdruck zu bringen. Dieser Urheberrechtsvermerk darf weder vom Kunden,
 * Nutzer und/oder von Dritten entfernt, veraendert oder disloziert werden.
 * =========================================================================*/

define(["underscore", "backbone"], function(_, Backbone) {

  /*
  ┃  Type/Interface MatchCriteria
  ┃    - definition: /javascript/apps/scripts/clientext.ts
  ┃
  ┃  Visualized type structure of type "MatchCriteria":
  ┃
  ┃      MatchCriteria  ◄━━━━━━━┳━━━━━━━━━━━━━━━━━━━━━━━━┓ 
  ┃        ┃                    ┃                        ┃
  ┃        ┣━━ AND  ━━► Array [ ╋ ]  ◄━━━━━━━━━━━┓       ┃
  ┃        ┣━━ OR   ━━► Array [ ┻ ]  ◄━━━━━━━━━━━╋━━┓    ┃
  ┃        ┗━━ PropsMatch                        ┃  ┃    ┃ 
  ┃              = key: PropValueMatch           ┃  ┃    ┃
  ┃                       ┃                      ┃  ┃    ┃
  ┃                       ┣━ AND ━━━[ same as ]━━┛  ┃    ┃
  ┃                       ┣━ OR  ━━━[ same as ]━━━━━┛    ┃ 
  ┃                       ┣━ NOT  ━━━━━━━━━━━━━► Array [ ┻ ]
  ┃                       ┗━ ValueMatch
  ┃                             ┃ 
  ┃                             ┣━ CmpValueMatch [eq, neq, gt, ge, lt, le]
  ┃                             ┗━ exists
  ┃
  */


  /* ---------------------------------------------------- +
   |  Logical Operator                                    |
   |   => Superclass for operators for multiple children  |
   + ---------------------------------------------------- */

   var LogicalOperator = function(operandExpressions) {
    this.operands = this._parseOperands(operandExpressions);
  }

  // Overwritte extend and also accept operator key
  LogicalOperator.extend = Backbone.Model.extend;

  _.extend(LogicalOperator.prototype, {
    _parseOperands: function(operandExpressions) { 
      if (!operandExpressions) return [];
      return _.map(operandExpressions, function(expression) {
        return new MatchCriteria(expression);
      });
    },

    _buildOperandResultProviders: function(resultProperty, propertyAccessor) {
      return _.map(
        this.operands, 
        function(matchCriteria) { 
          return _.bind(matchCriteria.matches, /* this context */matchCriteria, resultProperty, propertyAccessor); 
        }
      )
    },

    // has to be overriden by each implementing class
    _evaluateOperandResults: function(operandResultProviders) { return false; },

    evaluate: function(object, propertyAccessor) {
      var operandResultProviders = this._buildOperandResultProviders(object, propertyAccessor);
      return this._evaluateOperandResults(operandResultProviders);
    },

    getPropertyNames: function() {
      return _.chain(this.operands)
        .map(function(operand) { return operand.getPropertyNames(); })
        .flatten(true) // true = shallow flattening
        .uniq()
        .value();
    },

    toQueryConstraint: function() {
      var constraint = {};
      
      if (!this.expressionKey) return undefined;

      constraint[this.expressionKey] = _.map(this.operands, function(operand) {
        return operand.toQueryConstraint();
      });

      return constraint;
    }
  });


  /* ------------------------------------ +
   |  Logical Operator - Implementations  |
   + ------------------------------------ */

  var AndMatch = LogicalOperator.extend(
    {
      expressionKey: "and",
      _evaluateOperandResults: function(operandResultProviders) { 
        return _.every(operandResultProviders, function(provider) { return provider(); });
      },
    }
  );

  var OrMatch = LogicalOperator.extend(
    {
      expressionKey: "or",
      _evaluateOperandResults: function(operandResultProviders) { 
        return _.some(operandResultProviders, function(provider) { return provider(); });
      },
    }
  );

  var NotMatch = LogicalOperator.extend(
    {
      expressionKey: "not",
      _evaluateOperandResults: function(operandResultProviders) { 
        return !_.some(operandResultProviders, function(provider) { return provider(); });
      },
    } 
  );
  

  /* -------------------------- +
   |  MatchTypeHandlerRegistry  |
   + -------------------------- */

  var isNumber = function(value) {
    var valueAsNumber = new Number(value).valueOf()
    return !Number.isNaN(valueAsNumber)
  }

  var buildPropertyEqualsExpression = function(propertyName, propertyValue) {
    return {
      label: propertyName,
      regex: '^\\Q' + propertyValue + '\\E$'
    } 
  }

  var buildComparisonExpression = function(comparisonOperator, propertyName, propertyValue) {
    if (isNumber(propertyValue)) {
      return {
        label: propertyName,
        num: new Number(propertyValue).valueOf(),
        cmp: comparisonOperator
      };
    }

    // only supports numbers currently
    return {
      not: [
        { unparsed: "ALL" }
      ]
    }
  }

  const MatchTypeHandlerRegistry = { }

  // EQUAL
  MatchTypeHandlerRegistry.eq = {
    predicate: function(matchValue, propertyValue) { 
      return matchValue == propertyValue; 
    },
    
    buildQueryConstraint: buildPropertyEqualsExpression
  }

  // NOT EQUAL
  MatchTypeHandlerRegistry.neq = {
    predicate: function(matchValue, propertyValue) { 
      return matchValue != propertyValue; 
    },
    
    buildQueryConstraint: function(propertyName, propertyValue) {
      return { 
        not: [ 
          buildPropertyEqualsExpression(propertyName, propertyValue) 
        ]
      };
    }
  }

  // EXISTS
  MatchTypeHandlerRegistry.exists = {
    predicate: function(matchValue, propertyValue) { 
      return propertyValue != undefined; 
    },

    buildQueryConstraint: function(propertyName) {
      return { unparsed: propertyName + ":ALL" }
    }
  }

  // COMPARISON OPERATORS
  const COMPARISON_OPERATORS = ["le", "lt", "ge", "gt"];

  MatchTypeHandlerRegistry.le = {
    predicate: function(matchValue, propertyValue) { 
      if (!isNumber(matchValue) || !isNumber(propertyValue)) return false;
      return propertyValue <= matchValue; 
    },
  }

  MatchTypeHandlerRegistry.lt = {
    predicate: function(matchValue, propertyValue) { 
      if (!isNumber(matchValue) || !isNumber(propertyValue)) return false;
      return propertyValue < matchValue; 
    },
  }

  MatchTypeHandlerRegistry.ge = {
    predicate: function(matchValue, propertyValue) { 
      if (!isNumber(matchValue) || !isNumber(propertyValue)) return false;
      return propertyValue >= matchValue; 
    },
  }

  MatchTypeHandlerRegistry.gt = {
    predicate: function(matchValue, propertyValue) { 
      if (!isNumber(matchValue) || !isNumber(propertyValue)) return false;
      return propertyValue > matchValue; 
    },
  }

  _.each(COMPARISON_OPERATORS, function(operator) {
    // dynamically assing the "buildQueryConstraint" functions
    MatchTypeHandlerRegistry[operator].buildQueryConstraint = _.bind(
      buildComparisonExpression, 
      null, // no "this" context
      operator.toUpperCase() // fixate comparison operator parameter
    )
  });


  /* ------------------------------- +
   |  Property Value Match Handling  |
   + ------------------------------- */
  
  var ValueMatch = function(matchExpression) {
    this.matchType = Object.keys(matchExpression)[0];
    this.matchValue = matchExpression[this.matchType];

    this.matchTypeHandler = MatchTypeHandlerRegistry[this.matchType];
  };

  _.extend(ValueMatch.prototype, {
    evaluate: function(value) {
      if (!this.matchTypeHandler) return false;
      return this.matchTypeHandler.predicate(this.matchValue, value);
    },

    toQueryConstraint: function(propertyName) {
      if (!this.matchTypeHandler) return undefined;
      return this.matchTypeHandler.buildQueryConstraint(propertyName, this.matchValue);
    }
  });

  var PropertyMatch = function(matchExpression) {
    this.propertyName = Object.keys(matchExpression)[0];

    var matchValueExpression = matchExpression[this.propertyName];
    
    // currently only supports ValueMatch as PropValueMatch
    this.propertyValueMatch = new ValueMatch(matchValueExpression);
  };

  _.extend(PropertyMatch.prototype, {
    evaluate: function(object, propertyAccessor) {
      var propertyValue = propertyAccessor.call(this, object, this.propertyName);
      return this.propertyValueMatch.evaluate(propertyValue, propertyAccessor);
    },

    getPropertyNames: function() {
      return [this.propertyName];
    },

    toQueryConstraint: function() {
      return this.propertyValueMatch.toQueryConstraint(this.propertyName);
    }
  });


  /* --------------- +
   |  MatchCriteria  |
   + --------------- */

  const DEFAULT_PROPERTY_ACCESSOR = function(object, propertyName) {
    if (_.isFunction(object.get)) {
      var value = object.get(propertyName, { format: 'VALUE' });

      if (!_.isObject(value)) return value;
      if (value.str) return value.str;
      if (value.num) return value.num;

      return value;
    }
    
    return object[propertyName];
  }

  var createLogicalOperatorOrDefault = function(matchExpression, DefaultConstructor) {
    if (!_.isObject(matchExpression) || _.isEmpty(matchExpression)) {
      throw new TypeError("The given expression is not a valid MatchCriteria.");
    }

    var rawMatchExpression = matchExpression;
    var keys = Object.keys(matchExpression);
    var expressionKey = keys[0];
        
    var implicitAndToExpressionArray = function(expression) {
      return Object.keys(expression).map(
        function(expressionKey) {
          var operandExpression = {};
          operandExpression[expressionKey] = expression[expressionKey];
          return operandExpression;
        }
      );  
    }

    if (keys.length > 1) {
      // implicit AND expression - e.g. { categoryclass: {eq: 'contract'}, extension: {eq: 'pdf'} }
      // transform to explicit AND expression
      rawMatchExpression = {
        and: implicitAndToExpressionArray(matchExpression)
      }
      expressionKey = "and";
    }

    var isLogicalOperatorExression = function(expressionValue) {
      // the value of logical operators is required to be an array
      return Array.isArray(expressionValue);
    }

    var expressionValue = rawMatchExpression[expressionKey];

    if (isLogicalOperatorExression(expressionValue)) {
      switch(expressionKey) {
        case "and":
          return new AndMatch(rawMatchExpression.and);
        case "or":
          return new OrMatch(rawMatchExpression.or);
        case "not":
          return new NotMatch(rawMatchExpression.not);
      }  
    }

    return new DefaultConstructor(rawMatchExpression);
  }

  var MatchCriteria = function(matchExpression) {
    this.matchExpression = createLogicalOperatorOrDefault(matchExpression, PropertyMatch);
  };

  _.extend(MatchCriteria.prototype, {
    matches: function(object, propertyAccessor) {
      // uses DEFAULT_PROPERTY_ACCESSOR, which handles PropertyModels, if no accessor is specified.
      propertyAccessor = propertyAccessor || DEFAULT_PROPERTY_ACCESSOR;

      return this.matchExpression.evaluate(object, propertyAccessor);
    },

    getPropertyNames: function () {
      return this.matchExpression.getPropertyNames();
    },
    
    toQueryConstraint: function() {
      return this.matchExpression.toQueryConstraint();
    }
  });

  MatchCriteria.DEFAULT_PROPERTY_ACCESSOR = DEFAULT_PROPERTY_ACCESSOR;
  return MatchCriteria;
});
